import React, { useState, Dispatch, SetStateAction, useEffect } from 'react';
import axios from '../../../sharedModules/utils/axios.utils';
import { ChillerLog, ChillerJob, ChillerLogPhoto } from '../../model';

interface ChillerLogsContextInterface {
  chillerJob?: ChillerJob;
  setChillerJob?: Dispatch<SetStateAction<ChillerJob>>;

  historicalLogs?: ChillerLog[];

  logId?: string;
  setLogId?: Dispatch<SetStateAction<string>>;

  fetchChillerLogs?: (id: string) => void;

  chillerLog?: ChillerLog;
  setChillerLog?: Dispatch<SetStateAction<ChillerLog>>;
  submitChillerLogApproval?: (onSuccess?: any, onFailure?: any) => void;

  fetchChillerLogPhotos?: () => void;
  chillerLogPhotos?: ChillerLogPhoto[];

  prevChillerLog1?: ChillerLog;
  prevChillerLog2?: ChillerLog;

  saveCurrentLogToLocalStorage?: () => void;
  submitChillerLog?: (onSuccess?: any, onFailure?: any) => void;

  loadChillerLogsFromLocalStorage?: (id: string) => void;

  localUpdatedAt?: string;
  serverUpdatedAt?: string;

  previousLogDialogOpen?: boolean;
  setPreviousLogDialogOpen?: Dispatch<SetStateAction<boolean>>;

  logForPreviousLogModal?: ChillerLog;
  setLogForPreviousLogModal?: Dispatch<SetStateAction<ChillerLog>>;

  loadingDialogOpen?: boolean;
  setLoadingDialogOpen?: Dispatch<SetStateAction<boolean>>;

  jobSpreadsheetPageLoad?: (id: string) => void;

  currentLogApproved?: boolean;

  historicalTableIsPartial?: boolean;

  photoDeleteConfirmationModalOpen?: boolean;
  setPhotoDeleteConfirmationModalOpen?: Dispatch<SetStateAction<boolean>>;
  photoToDelete?: ChillerLogPhoto;
  setPhotoToDelete?: Dispatch<SetStateAction<ChillerLogPhoto>>;

  submitDialogOpen?: boolean;
  setSubmitDialogOpen?: Dispatch<SetStateAction<boolean>>;

  reviewDialogOpen?: boolean;
  setReviewDialogOpen?: Dispatch<SetStateAction<boolean>>;

  showRequiredFieldWarnings?: boolean;
  setShowRequiredFieldWarnings?: Dispatch<SetStateAction<boolean>>;
}

interface ChillerLogsContextProviderProps {
  children: React.ReactNode;
}

const ChillerLogsContext = React.createContext<ChillerLogsContextInterface>({});

const ChillerLogsContextConsumer = ChillerLogsContext.Consumer;
const ChillerLogsContextProvider: React.FC<ChillerLogsContextProviderProps> = ({ children }) => {
  let [logId, setLogId] = useState<string>(undefined);
  let [chillerJob, setChillerJob] = useState<ChillerJob | null>(null);
  let [historicalLogs, setHistoricalLogs] = useState<ChillerLog[]>([]);
  let [chillerLog, setChillerLog] = useState<ChillerLog>({});
  let [prevChillerLog1, setPrevChillerLog1] = useState<ChillerLog>({});
  let [prevChillerLog2, setPrevChillerLog2] = useState<ChillerLog>({});
  let [localUpdatedAt, setLocalUpdatedAt] = useState<string>(undefined);
  let [serverUpdatedAt, setServerUpdatedAt] = useState<string>(undefined);
  let [previousLogDialogOpen, setPreviousLogDialogOpen] = useState<boolean>(false);
  let [logForPreviousLogModal, setLogForPreviousLogModal] = useState<ChillerLog>({});
  let [loadingDialogOpen, setLoadingDialogOpen] = useState<boolean>(false);
  let [currentLogApproved, setCurrentLogApproved] = useState<boolean>(false);
  let [historicalTableIsPartial, setHistoricalTableIsPartial] = useState<boolean>(false);
  let [chillerLogPhotos, setChillerLogPhotos] = useState<ChillerLogPhoto[]>([]);
  let [photoDeleteConfirmationModalOpen, setPhotoDeleteConfirmationModalOpen] = useState(false);
  let [photoToDelete, setPhotoToDelete] = useState<ChillerLogPhoto>();
  let [submitDialogOpen, setSubmitDialogOpen] = useState<boolean>(false);
  let [reviewDialogOpen, setReviewDialogOpen] = useState<boolean>(false);
  let [showRequiredFieldWarnings, setShowRequiredFieldWarnings] = useState<boolean>(false);

  let formattedTime = (input: string): string => {
    const date = new Date(input);
    if (isNaN(date.getTime())) {
      return '';
    }

    return date.toLocaleString('en-US', {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  };

  const fetchServerUpdatedTime = async (id) => {
    try {
      let response = await axios.get<string, any>(`/chiller_logs/chiller_jobs/${id}/edit`);
      return response?.data?.updated_at;
    } catch (error) {
      console.error('Error fetching server update time:', error);
      return undefined;
    }
  };

  const fetchLocalUpdatedTime = (id) => {
    const savedValues = JSON.parse(localStorage.getItem('chillerLogSavedValues')) || {};
    return savedValues[id]?.savedToLocalAt;
  };

  let saveSingleChillerLogToLocalStorage = (logData: any, id: string) => {
    if (!id) {
      console.log('No ID.  Cannot save Chiller Log to local storage.');
      return;
    }

    let newLocalUpdatedAt = new Date().toISOString();

    const logWithUpdateTime = {
      ...logData,
      savedToLocalAt: newLocalUpdatedAt,
    };

    const savedValues = JSON.parse(localStorage.getItem('chillerLogSavedValues')) || {};
    savedValues[id] = logWithUpdateTime;
    localStorage.setItem('chillerLogSavedValues', JSON.stringify(savedValues));
  };

  const saveCurrentLogToLocalStorage = () => {
    let newLocalUpdatedAt = new Date().toISOString();
    let formattedLocalTime = formattedTime(newLocalUpdatedAt);
    setLocalUpdatedAt(formattedLocalTime);

    let currentLogWithPrevIds = {
      ...chillerLog,
      prevChillerLog1Id: prevChillerLog1?.id,
      prevChillerLog2Id: prevChillerLog2?.id,
    };

    saveSingleChillerLogToLocalStorage(currentLogWithPrevIds, logId);
    console.log('Saved current chiller log to local storage');
  };

  const makeHistoricalTableWithTwoPreviousLogs = (savedPrevLog1, savedPrevLog2) => {
    // This function is not called when the user loads from the server.
    // We only call this when they load from localStorage without a server load first
    // Because that leaves the historical logs empty

    // Here, we use the two previous logs for the table of historical logs.
    // If there are more than two logs, they won't be included from a localStorage load, but can be fetched from a server load.

    if (historicalLogs?.length) return;

    const buildSummary = (log: any) =>
      log && {
        id: log.id,
        tech: log.emp_id,
        status: log.status,
        submitted_date: log.submit_dt,
      };

    const previousLogs = [buildSummary(savedPrevLog1), buildSummary(savedPrevLog2)].filter(Boolean); // Filter out any undefined logs

    setHistoricalLogs(previousLogs);
    setHistoricalTableIsPartial(true);
  };

  const loadChillerLogsFromLocalStorage = (id) => {
    const savedValues = JSON.parse(localStorage.getItem('chillerLogSavedValues')) || {};
    setChillerLog(savedValues[id]);

    let prevId1 = savedValues[id]?.prevChillerLog1Id;
    let prevId2 = savedValues[id]?.prevChillerLog2Id;
    let savedPrevLog1 = savedValues[prevId1];
    let savedPrevLog2 = savedValues[prevId2];

    setPrevChillerLog1(savedPrevLog1);
    setPrevChillerLog2(savedPrevLog2);

    makeHistoricalTableWithTwoPreviousLogs(savedPrevLog1, savedPrevLog2);
  };

  const fetchChillerLogs = async (id) => {
    try {
      const response0: any = await axios.get(`/chiller_logs/chiller_logs/${id}`);
      const allLogs = response0?.data?.result;
      const spreadsheetDetails = structuredClone(allLogs?.spreadsheet_details); // deep clone for safety

      const prevChillerLog1Id = allLogs?.prev_chiller_log1_id;
      const prevChillerLog2Id = allLogs?.prev_chiller_log2_id;

      let prev1 = null;
      let prev2 = null;

      if (prevChillerLog1Id) {
        const response1: any = await axios.get(`/chiller_logs/chiller_logs/${prevChillerLog1Id}`);
        prev1 = structuredClone(response1?.data?.result?.spreadsheet_details);
        saveSingleChillerLogToLocalStorage(prev1, prev1?.id);
      }

      if (prevChillerLog2Id) {
        const response2: any = await axios.get(`/chiller_logs/chiller_logs/${prevChillerLog2Id}`);
        prev2 = structuredClone(response2?.data?.result?.spreadsheet_details);
        saveSingleChillerLogToLocalStorage(prev2, prev2?.id);
      }

      setChillerJob(response0?.data?.result?.chiller_job_detail);
      setChillerLog(spreadsheetDetails);
      setPrevChillerLog1(prev1);
      setPrevChillerLog2(prev2);
      setHistoricalLogs(response0?.data?.historical_logs);
      setHistoricalTableIsPartial(false);
      setCurrentLogApproved(spreadsheetDetails?.status === 'approved');

      return spreadsheetDetails;
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  const fetchLogsAndSaveInitial = async (id) => {
    let fetchedChillerLog = await fetchChillerLogs(id);

    let savedValues = JSON.parse(localStorage.getItem('chillerLogSavedValues')) || {};
    let currentLogMissingFromLocal = !savedValues[id];

    if (fetchedChillerLog && currentLogMissingFromLocal) {
      saveSingleChillerLogToLocalStorage(fetchedChillerLog, fetchedChillerLog?.id);
    }
  };

  const determineLoadingDialogOrServerFetch = (id, newServerUpdatedAt, newLocalUpdatedAt) => {
    if (newServerUpdatedAt && newLocalUpdatedAt && newServerUpdatedAt < newLocalUpdatedAt) {
      setLoadingDialogOpen(true);
    } else {
      setLoadingDialogOpen(false);
      fetchLogsAndSaveInitial(id);
    }
  };

  const deleteOldLogsFromLocalStorage = () => {
    const rawSavedValues = localStorage.getItem('chillerLogSavedValues');
    if (!rawSavedValues) return;

    const savedValues: Record<string, { savedToLocalAt: string }> = JSON.parse(rawSavedValues);
    const cutoffTime = Date.now() - 30 * 24 * 60 * 60 * 1000; // 30 days ago

    const filteredEntries = Object.entries(savedValues).filter(([_, value]) => {
      const savedTime = new Date(value.savedToLocalAt).getTime();
      return savedTime >= cutoffTime;
    });

    if (filteredEntries.length > 0) {
      localStorage.setItem('chillerLogSavedValues', JSON.stringify(Object.fromEntries(filteredEntries)));
    } else {
      localStorage.removeItem('chillerLogSavedValues');
    }
  };

  const jobSpreadsheetPageLoad = async (id) => {
    let newServerUpdatedAt = await fetchServerUpdatedTime(id);
    setServerUpdatedAt(formattedTime(newServerUpdatedAt));

    let newLocalUpdatedAt = fetchLocalUpdatedTime(id);
    setLocalUpdatedAt(formattedTime(newLocalUpdatedAt));

    deleteOldLogsFromLocalStorage();

    determineLoadingDialogOrServerFetch(id, newServerUpdatedAt, newLocalUpdatedAt);
  };

  const submitChillerLog = (onSuccess?: any, onFailure?: any) => {
    try {
      saveSingleChillerLogToLocalStorage(chillerLog, chillerLog?.id);
      axios
        .put(`/chiller_logs/chiller_logs/${chillerLog.id}`, { ...chillerLog, status: 'pending_approval' })
        .then((response) => {
          if (onSuccess) {
            onSuccess();
          }
        })
        .catch((error) => {
          if (onFailure) {
            onFailure();
          }
          console.error('Error submitting chiller log:', error);
        });
    } catch (error) {
      console.error('Error submitting chiller log:', error);
    }
  };

  const submitChillerLogApproval = (onSuccess?: any, onFailure?: any) => {
    try {
      axios
        .put(`/chiller_logs/chiller_logs/${chillerLog.id}`, { ...chillerLog, status: 'approved' })
        .then((response) => {
          if (onSuccess) {
            onSuccess();
          }
        })
        .catch((error) => {
          if (onFailure) {
            onFailure();
          }
          console.error('Error submitting chiller log:', error);
        });
    } catch (error) {
      console.error('Error submitting chiller log:', error);
    }
  };

  const fetchChillerLogPhotos = async () => {
    let response: any = await axios.get('chiller_logs/chiller_log_photos', {
      params: { chiller_log_id: chillerLog?.id },
    });
    setChillerLogPhotos(response?.data?.result);
  };

  return (
    <ChillerLogsContext.Provider
      value={{
        logId,
        setLogId,

        chillerLog,
        setChillerLog,

        chillerJob,
        setChillerJob,
        historicalLogs,
        fetchChillerLogs,

        fetchChillerLogPhotos,
        chillerLogPhotos,

        prevChillerLog1,
        prevChillerLog2,

        saveCurrentLogToLocalStorage,
        submitChillerLog,
        submitChillerLogApproval,
        loadChillerLogsFromLocalStorage,

        localUpdatedAt,
        serverUpdatedAt,

        previousLogDialogOpen,
        setPreviousLogDialogOpen,

        logForPreviousLogModal,
        setLogForPreviousLogModal,

        loadingDialogOpen,
        setLoadingDialogOpen,

        jobSpreadsheetPageLoad,

        currentLogApproved,

        historicalTableIsPartial,

        photoDeleteConfirmationModalOpen,
        setPhotoDeleteConfirmationModalOpen,
        photoToDelete,
        setPhotoToDelete,

        submitDialogOpen,
        setSubmitDialogOpen,

        reviewDialogOpen,
        setReviewDialogOpen,

        showRequiredFieldWarnings,
        setShowRequiredFieldWarnings,
      }}
    >
      {children}
    </ChillerLogsContext.Provider>
  );
};

export { ChillerLogsContextProvider, ChillerLogsContextConsumer, ChillerLogsContext };
